/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import { Popup, PopupSection, PopupActionList } from '../../../Popup';
import { InlineEditDropdown, INLINE_EDIT_POPUP_COLLISION } from '../shared/InlineEditDropdown';
import { Checkbox } from '../../Checkbox';
import { Tooltip } from '../../../../atoms/Tooltip';
import './InlineFlagsSelectStyles.scss';
/**
 * InlineFlagsSelect Component
 * Click-to-edit inline dropdown showing a summary string, opening a popover of independent
 * checkboxes — for boolean-flag sets (e.g. visibility toggles) rather than single/multi selection
 * from a list of mutually exclusive options (see `InlineSelect` for that case).
 * @version 1.0.0
 * @since 2026-09-29
 */
export function InlineFlagsSelect({ options, values, indeterminateValues = [], summary, muted = false, onChange, disabled = false, ariaLabel, note }) {
    const [visible, setVisible] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    // React 17-compatible stable id (no useId() until React 18) so multiple instances rendered in a
    // list (e.g. one per table row) don't collide on checkbox input ids.
    const uniqueIdRef = useRef();
    if (!uniqueIdRef.current) {
        uniqueIdRef.current = `qmb-ui-inline-flags-select-${Math.random().toString(36).substr(2, 9)}`;
    }
    const uniqueId = uniqueIdRef.current;
    const handleOpen = () => {
        if (disabled)
            return;
        setVisible(true);
    };
    const handleClose = () => {
        setVisible(false);
    };
    const handleToggle = (option, checked) => {
        if (option.disabled)
            return;
        const nextValues = checked
            ? Array.from(new Set([...values, option.value]))
            : values.filter((v) => v !== option.value);
        onChange(nextValues);
    };
    return (React.createElement("div", { className: "qmb-ui-inline-flags-select" },
        React.createElement(InlineEditDropdown, { onClick: handleOpen, refComp: setAnchorEl, disableEdit: disabled, width: "full", "aria-haspopup": "true", "aria-expanded": visible, "aria-label": ariaLabel },
            React.createElement("span", { className: muted
                    ? 'qmb-ui-inline-flags-select__summary qmb-ui-inline-flags-select__summary--muted'
                    : 'qmb-ui-inline-flags-select__summary' }, summary)),
        React.createElement(Popup, { position: "bottom-left", anchor: anchorEl, show: visible, onClose: handleClose, collision: INLINE_EDIT_POPUP_COLLISION, containerClass: "qmb-ui-popup--action-list qmb-ui-inline-flags-select__popup" },
            React.createElement(PopupSection, null,
                note && React.createElement("p", { className: "qmb-ui-inline-flags-select__note" }, note),
                React.createElement(PopupActionList, null, options.map((option) => {
                    const checkbox = (React.createElement(Checkbox, { id: `${uniqueId}-${option.value}`, checked: values.includes(option.value), indeterminate: indeterminateValues.includes(option.value), disabled: option.disabled, onChange: (checked) => handleToggle(option, checked), label: option.label }));
                    // A disabled input fires no pointer events, so the tooltip has to hang off a
                    // wrapper rather than the checkbox itself or it would never show. Positioned above
                    // rather than beside: the wrapper spans the popover's full width, so "right" lands
                    // the tooltip on top of the option it is explaining.
                    return option.disabled && option.disabledReason ? (React.createElement(Tooltip, { key: option.value, content: option.disabledReason, position: "top", multiline: true },
                        React.createElement("span", { className: "qmb-ui-inline-flags-select__disabled-option" }, checkbox))) : (React.createElement(React.Fragment, { key: option.value }, checkbox));
                }))))));
}
